import { useState, useEffect } from 'react';

import { Button } from '@/components';
import { TextInput } from '@/components/text-input';
import { useAppContext } from '@/context';
import { useLogin } from '@/hooks';
import { emailRegex } from '@/utils/validation';
import { getTargetFromStorage } from '@/utils/auth';

import { Trans, useTranslation } from 'react-i18next';
import { TranslatedLink } from '@/utils/translatedComponents';
import MailIcon from '../../../public/assets/login_mail.svg';
import Logo from '../../../public/beatly.svg';
import styles from './Login.module.css';

export function Login() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [error, setError] = useState('');
  const { setAlert } = useAppContext();
  const { t } = useTranslation();

  const { mutate } = useLogin({
    email,
    target: typeof window !== 'undefined' ? getTargetFromStorage() : undefined,
    onSuccess: ({ code }: ResponseObject) => {
      if (code === 'USER_NOT_FOUND') {
        setAlert('ACCOUNT_NOT_FOUND');
        setIsLoading(false);
        return;
      }

      if (code === 'SENT_LOGIN_MAGICLINK') {
        if (linkSent) {
          setAlert('EMAIL_WAS_RESENT');
        } else {
          setIsLoading(false);
          setLinkSent(true);
        }
        return;
      }
      setAlert('INTERNAL_SERVER_ERROR');
      setIsLoading(false);
    }
  });

  // Sync state from DOM when browser autofill/password manager fills the email (they don't fire onChange)
  useEffect(() => {
    const syncFromDom = () => {
      const input = document.getElementById('login-email') as HTMLInputElement | null;
      if (input?.value && input.value !== email) {
        setEmail(input.value);
      }
    };
    syncFromDom();
    const timeoutId = window.setTimeout(syncFromDom, 200);
    return () => window.clearTimeout(timeoutId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount to capture autofill

  // Redirect logic is handled by index.tsx - no need for it here

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!emailRegex.test(trimmed)) {
      setError(t('alert.INVALID_EMAIL') || '');
      return;
    }
    setIsLoading(true);
    mutate();
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (error) setError('');
    setEmail(event.target.value);
  }

  function resendData() {
    mutate();
  }

  function resetData() {
    setEmail('');
    setLinkSent(false);
  }

  return (
    <main className={styles.login}>
      <Logo className={styles.loginLogo} />

      {!linkSent ? (
        <div className={styles.loginForm}>
          <div className={styles.loginFormHeader}>
            <h2 className={styles.loginHeaderTitle}>{t('login.title')}</h2>
            <div className={styles.loginHeaderDescription}>{t('login.subtitle')}</div>
          </div>
          <div className={styles.loginRow}>
            <TextInput
              id="login-email"
              label={t('login.email') as string}
              value={email}
              placeholder={t('placeholder.emailExample')}
              onChange={onChange}
              required
              name="email"
              error={error}
            />
          </div>

          <div className={styles.loginRow} data-row="submit">
            <Button
              isLoading={isLoading}
              disabled={isLoading || !!error || !email.trim()}
              type="submit"
              theme="cta"
              onClick={onSubmit}
            >
              {t('login.button')}
            </Button>
          </div>

          <div className={styles.loginRowCenter}>
            <Trans
              i18nKey="login.register"
              components={{
                1: <TranslatedLink href="/signup" key="login-register" />
              }}
            />
          </div>
        </div>
      ) : (
        <div className={`${styles.loginForm} ${styles.loginInfo}`}>
          <MailIcon className={styles.loginBottomSpacing} />
          <h3 className={styles.loginInfoTitle}>{t('login.info.title')}</h3>
          <div className={styles.loginInfoSubtitle}>
            <Trans
              className={styles.loginInfoSubtitle}
              i18nKey="login.info.subtitle"
              components={{
                1: <b />
              }}
              values={{ email }}
            />
          </div>

          <div className={`${styles.loginInfoSubtitle} ${styles.loginBottomSpacing}`}>
            {t('login.info.reminder')}
          </div>
          <div className={`${styles.loginInfoFooter}  ${styles.loginBottomSpacing}`}>
            <Trans
              i18nKey="login.info.resend"
              components={{
                1: <span onClick={resendData} />
              }}
            />
          </div>
          <div className={styles.loginInfoFooter}>
            <Trans
              i18nKey="login.info.footer"
              components={{
                1: <span onClick={resetData} />
              }}
            />
          </div>
        </div>
      )}
    </main>
  );
}
